import { constants, utils } from '@frontend/partviewer';

import { FEATURE_IGNORE_KEY, MATERIAL_INDICATOR } from './constants';

const { SPLIT_CHAR, UNITS } = constants;
const { constructFeatureComparatorString, constructFeatureDescriptorString } =
  utils;

export const getObjectViewerId = (viewerIndicator, node, instanceDict) => {
  return [viewerIndicator, node.viewerId, instanceDict[node.viewerId]].join(
    '*',
  );
};

/*
This function is used to encode the viewer state for purposes snapping to
for comments. As a result, we will only want to pass through objects
with permanence in the tech data package. Ephemeral renderings like material
widget display should not be saved into the state.
*/
export const getEncodedCADViewerState = (state) => {
  const {
    activeBodies,
    enabledBodies,
    expandedBodies,
    explodedState,
    focusedBodies,
    focusedUuid,
    selectedBodies,
    showAdditionalKeysExplicit,
    visibleBodies,
  } = state;
  return {
    ab: activeBodies.filter((id) => id.indexOf(MATERIAL_INDICATOR) === -1),
    enb: enabledBodies,
    exb: expandedBodies,
    exs: explodedState,
    fb: focusedBodies.filter((id) => id.indexOf(MATERIAL_INDICATOR) === -1),
    fu: focusedUuid,
    sake: showAdditionalKeysExplicit.map((key) => {
      const pieces = key.split(SPLIT_CHAR);
      return `${pieces[1]}${SPLIT_CHAR}${pieces[2]}`;
    }),
    sb: selectedBodies,
    vb: visibleBodies.filter((id) => id.indexOf(MATERIAL_INDICATOR) === -1),
  };
};

export const decodeEncodedCADViewerState = (encodedState) => {
  const {
    ab: activeBodies,
    enb: enabledBodies,
    exb: expandedBodies,
    exs: explodedState,
    fb: focusedBodies,
    fu: focusedUuid,
    sake: showAdditionalKeysExplicitInfo,
    sb: selectedBodies,
    vb: visibleBodies,
  } = encodedState;
  const out = {};
  if (activeBodies) out.activeBodies = activeBodies;
  if (enabledBodies) out.enabledBodies = enabledBodies;
  if (expandedBodies) out.expandedBodies = expandedBodies;
  if (explodedState) out.explodedState = explodedState;
  if (focusedBodies) out.focusedBodies = focusedBodies;
  if (focusedUuid) out.focusedUuid = focusedUuid;
  if (selectedBodies) out.selectedBodies = selectedBodies;
  if (visibleBodies) out.visibleBodies = visibleBodies;
  if (showAdditionalKeysExplicitInfo && focusedUuid) {
    out.showAdditionalKeysExplicit = [];
    for (const key of showAdditionalKeysExplicitInfo) {
      out.showAdditionalKeysExplicit.push(`${focusedUuid}${SPLIT_CHAR}${key}`);
    }
  }
  return out;
};

export const groupFeaturesByFamily = (features) => {
  const featuresByFamily = new Map();
  for (const feature of features || []) {
    const family = feature.family;
    const group = featuresByFamily.get(family);
    if (group) {
      group.children.push(feature);
    } else {
      featuresByFamily.set(family, { family, feature, children: [feature] });
    }
  }
  return Array.from(featuresByFamily.values()).sort((a, b) => {
    const _a = a.family.toLowerCase();
    const _b = b.family.toLowerCase();
    return _a < _b ? -1 : _a > _b ? 1 : 0;
  });
};

const groupFeaturesByProperties = (
  features,
  displayUnits,
  nativeUnits,
  dimensionalPrecision,
  displayScale,
) => {
  const featuresByComparator = new Map();
  for (const feature of features || []) {
    const comparator = constructFeatureComparatorString(
      feature,
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
    );
    const group = featuresByComparator.get(comparator);
    if (group) {
      group.children.push(feature);
    } else {
      featuresByComparator.set(comparator, {
        feature,
        children: [feature],
        comparator,
        sortObject: Object.fromEntries(
          feature.dbData.map(({ name, value }) => [name, value]),
        ),
      });
    }
  }

  return Array.from(featuresByComparator.values()).sort((a, b) => {
    //sort by these sortObject.props (in descending order)
    for (const prop of ['volume', 'area', 'length', 'radius', 'cut_length']) {
      const _aValue = a.sortObject[prop];
      const _bValue = b.sortObject[prop];
      if (_aValue > _bValue) {
        return -1;
      } else if (_aValue < _bValue) {
        return 1;
      }
      //otherwise continue to compare next prop
    }
    return 0; //equal
  });
};

/*
Will group either geometric features or manufacturability feedback into form
for antd tree component. Initially grouped by family, then grouped by properties
for second level.
*/
export const getFeatureTreeData = (
  features,
  displayUnits = UNITS.IN,
  nativeUnits = UNITS.MM,
  dimensionalPrecision = 3,
  displayScale = 1.0,
) => {
  const familyGroups = groupFeaturesByFamily(features);
  const data = [];
  for (const familyGroup of familyGroups) {
    const color = familyGroup.feature.color;
    const isUniformColor = !familyGroup.children.some((f) => f.color !== color);
    const familyEntry = {
      key: familyGroup.family + FEATURE_IGNORE_KEY,
      title: `${familyGroup.family} (${familyGroup.children.length})`,
      color: isUniformColor ? color : null,
      children: [],
      feature: familyGroup.feature,
      message: familyGroup.feature.message,
      canSnap: false,
    };

    const propertiesGroups = groupFeaturesByProperties(
      familyGroup.children,
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
    );
    for (const propertiesGroup of propertiesGroups) {
      const dataString = constructFeatureDescriptorString(
        propertiesGroup.feature,
        displayUnits,
        nativeUnits,
        dimensionalPrecision,
        displayScale,
      );
      if (propertiesGroup.children.length > 1) {
        const propsEntry = {
          key: propertiesGroup.feature.key + FEATURE_IGNORE_KEY,
          title: `${
            dataString ? dataString : propertiesGroup.feature.family
          } (${propertiesGroup.children.length})`,
          color: propertiesGroup.feature.color,
          children: [],
          canSnap: false,
        };
        for (const feature of propertiesGroup.children) {
          propsEntry.children.push({
            children: [],
            title: feature.family,
            ...feature,
          });
        }
        familyEntry.children.push(propsEntry);
      } else {
        familyEntry.children.push({
          children: [],
          title: dataString ? dataString : propertiesGroup.feature.family,
          ...propertiesGroup.feature,
        });
      }
    }
    data.push(familyEntry);
  }
  return data;
};
